import { useFormContext } from 'react-hook-form';
import { Icon } from '@scripsteam/scrips-signal-ds';
import { useMemo } from 'react';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface FlagsSectionProps {
  /** Opens the full-screen Flags picker (Flutter `flags_selection_widget`). */
  onEdit?: () => void;
}

/**
 * FlagsSection — bulleted-text card matching Flutter
 * `create_appointment_widget.dart:1681–1742` + `flags_widget.dart`.
 *
 * Source of truth: form's `patientFlags` (auto-populated from
 * PreBookSelectedPatient and edited via the picker). Flutter shows the
 * same bulleted line with a pencil to open the picker; Save in the
 * picker writes back to this list.
 */
export function FlagsSection({ onEdit }: FlagsSectionProps) {
  const { watch } = useFormContext<AppointmentFormValues>();
  const patientFlags = watch('patientFlags');

  // Render-side dedupe — defensive against any path that bypasses the
  // patient-section's dedupe (e.g. a future form-level reset).
  const uniqueDisplays = useMemo(() => {
    if (!patientFlags?.length) return [] as string[];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const f of patientFlags) {
      const display = (f.display ?? '').trim();
      const key = display.toLowerCase();
      if (!display || seen.has(key)) continue;
      seen.add(key);
      out.push(display);
    }
    return out;
  }, [patientFlags]);

  if (uniqueDisplays.length === 0) return null;

  return (
    <div className="booking-section" data-slot="booking-section">
      <p className="booking-section-label">
        Flags
      </p>
      <div className="booking-card flex items-start">
        <Icon name="flag" size={20} aria-hidden />
        <p className="flex-1 text-sm text-text-primary leading-relaxed">
          {uniqueDisplays.join(' • ')}
        </p>
        {onEdit ? (
          <button type="button" aria-label="Edit flags" onClick={onEdit} className="booking-icon-btn"><Icon name="edit" size={16} aria-hidden /></button>
        ) : null}
      </div>
    </div>
  );
}
